/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Turns the full-page ADF viewer navigation into our own in-layout document
 * view.
 *
 * ACA opens a file by routing to a named outlet (`(viewer:view/<id>)`) that
 * renders the viewer as a full-screen overlay you have to close. We listen for
 * that navigation, cancel it, and redirect to `/preview/:id` — a normal shell
 * child route, so the document view renders inside the content area with the
 * sidebar still in place (see FilePreviewPageComponent).
 */
@Injectable({ providedIn: 'root' })
export class PreviewRedirectService {
  private readonly router = inject(Router);

  /** Allows the next viewer navigation through (used to open the native viewer). */
  private bypass = false;
  /** Last list/folder URL a file was opened from, used as the "back" target. */
  private lastBase = '/personal-files';

  init(): void {
    this.router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe((e) => this.intercept(e.url));
  }

  private intercept(url: string): void {
    const idx = url.indexOf('(viewer:');
    if (idx === -1) {
      return;
    }
    if (this.bypass) {
      this.bypass = false;
      return;
    }

    const match = url.match(/\(viewer:(?:view|preview)\/([^/)]+)/);
    if (!match) {
      return;
    }

    const nodeId = decodeURIComponent(match[1]);
    let base = url.substring(0, idx).replace(/\/$/, '');
    if (!base) {
      base = '/personal-files';
    }
    this.lastBase = base;

    // Cancel the overlay viewer navigation and show our in-layout view instead.
    // NB: the route is `/document/:id`, not `/preview/:id` — ACA's shell hides
    // the sidenav for any URL containing `/preview/` (hideSidenavConditions).
    this.router.navigate(['/document', nodeId], { queryParams: { back: base } });
  }

  /** Opens the stock ADF full-screen viewer for a node (bypasses the redirect). */
  openNative(nodeId: string, base?: string): void {
    const target = (base || this.lastBase || '/personal-files').replace(/\/$/, '');
    this.bypass = true;
    this.router.navigateByUrl(`${target}/(viewer:view/${nodeId})`);
  }
}
